import { Check, Signal, Wifi, Zap, Flame, MessageCircle, Sparkles, Infinity } from "lucide-react";
import { Reveal } from "../landing/Reveal";
import { MOBILE, MOBILE_NOTE, FIBRA, ENERGY_PACKS, ENERGY_TARIFFS, waFor } from "../../lib/offers";

const Price = ({ value, old }) => (
  <div className="flex items-end gap-2">
    {old && <span className="text-neutral-400 line-through text-lg mb-1">{old}€</span>}
    <span className="font-display font-extrabold text-5xl tracking-tight">{value}€</span>
    <span className="text-neutral-500 mb-2">/mese</span>
  </div>
);

const SectionHead = ({ label, title, sub }) => (
  <Reveal>
    <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-brand">{label}</p>
    <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl mt-3 max-w-3xl">{title}</h2>
    {sub && <p className="mt-4 text-lg text-neutral-500 max-w-2xl">{sub}</p>}
  </Reveal>
);

export const OfferteMobile = () => (
  <section id="offerte-mobile" data-testid="offerte-mobile-section" className="bg-white py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHead label="Offerte mobile" title="La tariffa giusta, senza sorprese." sub={MOBILE_NOTE} />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {MOBILE.map((o, i) => (
          <Reveal key={i} delay={(i % 3) * 0.06}>
            <div data-testid={`mobile-offer-${i}`} className={`group relative h-full rounded-3xl p-8 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${o.featured ? "bg-ink text-white shadow-2xl" : o.custom ? "bg-brand-soft border border-brand/20" : "bg-[#FAFAFA] border border-neutral-100 hover:shadow-2xl hover:shadow-black/5"}`}>
              {o.featured && <span className="absolute top-6 right-6 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-brand"><Sparkles className="w-3.5 h-3.5" /> Top</span>}
              <div className="flex items-center gap-2">
                <Signal className={`w-5 h-5 ${o.featured ? "text-brand" : "text-brand"}`} />
                <span className="text-sm font-bold uppercase tracking-widest opacity-70">{o.operator}</span>
              </div>
              <h3 className="font-display font-bold text-2xl mt-4">{o.name}</h3>
              <p className={`mt-1 font-display font-extrabold text-4xl tracking-tight ${o.custom ? "text-brand" : ""}`}>{o.giga}</p>
              <p className={`mt-3 leading-relaxed ${o.featured ? "text-white/70" : "text-neutral-500"}`}>{o.extra}</p>
              {o.featured && (
                <p data-testid={`mobile-offer-unlimited-${i}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand/15 text-brand text-sm font-semibold px-3 py-1.5 w-fit">
                  <Infinity className="w-4 h-4" /> Giga illimitati con la linea internet
                </p>
              )}
              <div className="mt-auto pt-6">
                {o.price ? <Price value={o.price} /> : <p className="font-display font-bold text-2xl text-brand">Prezzo su misura</p>}
                <a href={waFor(`Offerta mobile ${o.operator} ${o.name}`)} target="_blank" rel="noreferrer" data-testid={`mobile-offer-cta-${i}`}
                  className={`mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all duration-200 hover:scale-[1.02] ${o.featured || o.custom ? "bg-brand hover:bg-brand-hover text-white" : "bg-ink hover:bg-brand text-white"}`}>
                  <MessageCircle className="w-5 h-5" /> {o.custom ? "Chiedi la tua tariffa" : "Attiva su WhatsApp"}
                </a>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Fibra = () => (
  <section id="fibra" data-testid="fibra-section" className="bg-[#FAFAFA] py-24 md:py-32 border-y border-neutral-100">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHead label="Fibra casa" title="Internet ultraveloce, zero pensieri." sub="Attivazione gestita da noi, su tutte le tecnologie FTTH e FTTC." />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {FIBRA.map((f, i) => (
          <Reveal key={i} delay={(i % 4) * 0.05}>
            <div data-testid={`fibra-offer-${i}`} className={`group relative h-full rounded-3xl p-8 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${f.featured ? "bg-ink text-white shadow-2xl" : "bg-white border border-neutral-100 hover:shadow-2xl hover:shadow-black/5"}`}>
              <div className="flex items-center gap-2">
                <Wifi className="w-5 h-5 text-brand" />
                <span className="text-sm font-bold uppercase tracking-widest opacity-70">{f.operator}</span>
              </div>
              <h3 className="font-display font-bold text-xl mt-4">{f.tier}</h3>
              <p className={`text-sm mt-1 ${f.featured ? "text-white/60" : "text-neutral-500"}`}>{f.speed}</p>
              {f.tech && <p className={`text-sm mt-1 ${f.featured ? "text-brand" : "text-neutral-800 font-medium"}`}>{f.tech}</p>}
              <div className="mt-5"><Price value={f.price} old={f.oldPrice} /></div>
              <ul className="mt-5 space-y-2.5">
                {f.perks.map((p) => (
                  <li key={p} className={`flex gap-2 text-sm ${f.featured ? "text-white/80" : "text-neutral-600"}`}>
                    <Check className="w-4 h-4 text-brand shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
              <a href={waFor(`Fibra ${f.operator} ${f.tier}`)} target="_blank" rel="noreferrer" data-testid={`fibra-offer-cta-${i}`}
                className={`mt-auto pt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all duration-200 hover:scale-[1.02] ${f.featured ? "bg-brand hover:bg-brand-hover text-white" : "bg-ink hover:bg-brand text-white"}`}>
                <MessageCircle className="w-5 h-5" /> Attiva su WhatsApp
              </a>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const LuceGas = () => (
  <section id="luce-gas" data-testid="luce-gas-section" className="bg-white py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHead label="Luce e Gas" title="Bollette più leggere, condizioni chiare." sub={ENERGY_PACKS.note} />

      <Reveal>
        <div className="mt-12 rounded-3xl bg-ink text-white p-8 md:p-10">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl bg-yellow-400 flex items-center justify-center"><Zap className="w-6 h-6 text-ink" /></span>
            <h3 className="font-display font-bold text-xl md:text-2xl">{ENERGY_PACKS.title}</h3>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {ENERGY_PACKS.items.map((p, i) => (
              <div key={i} data-testid={`energy-pack-${i}`} className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:border-brand/40 transition-colors">
                <p className="font-display font-extrabold text-3xl">{p.price}€<span className="text-base text-white/50 font-sans font-normal">/mese</span></p>
                <p className="mt-2 text-sm text-white/60">{p.range}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        {ENERGY_TARIFFS.map((t, i) => (
          <Reveal key={i} delay={i * 0.06}>
            <div data-testid={`energy-tariff-${i}`} className="h-full rounded-3xl bg-[#FAFAFA] border border-neutral-100 p-8 hover:shadow-2xl hover:shadow-black/5 transition-shadow">
              <div className="flex items-center justify-between gap-3">
                <div className="h-14 w-32 rounded-xl bg-white border border-neutral-100 flex items-center justify-center overflow-hidden">
                  <img src={t.logo} alt={t.operator} className="max-h-11 max-w-[85%] object-contain" />
                </div>
                <span className="text-xs font-semibold text-brand bg-brand-soft rounded-full px-3 py-1 shrink-0">{t.lock}</span>
              </div>
              <p className="mt-4 text-sm font-bold uppercase tracking-widest text-neutral-800">{t.operator}</p>
              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-brand mt-1" />
                  <div>
                    <p className="font-display font-bold text-2xl">{t.luce.rate}</p>
                    <p className="text-sm text-neutral-500">Luce · {t.luce.fee}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Flame className="w-5 h-5 text-brand mt-1" />
                  <div>
                    <p className="font-display font-bold text-2xl">{t.gas.rate}</p>
                    <p className="text-sm text-neutral-500">Gas · {t.gas.fee}</p>
                  </div>
                </div>
              </div>
              <a href={waFor(`Luce e Gas ${t.operator}`)} target="_blank" rel="noreferrer" data-testid={`energy-cta-${i}`}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink hover:bg-brand text-white px-6 py-3.5 font-semibold transition-all duration-200 hover:scale-[1.02]">
                <MessageCircle className="w-5 h-5" /> Richiedi preventivo
              </a>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
